/**
 * @typedef {import('plugins/chatview/types').HeadingButtonAttributes} HeadingButtonAttributes
 * @typedef {import('@converse/headless').Message} Message
 * @typedef {import('@converse/headless').MUCMessage} MUCMessage
 * @typedef {import('lit').TemplateResult} TemplateResult
 */
import { api, converse } from '@converse/headless';
import { html } from 'lit';
import { until } from 'lit/directives/until.js';
import { MOBILE_CUTOFF } from 'shared/constants.js';
import tplNewDay from './templates/new-day.js';
import { BUNDLED_EMOJI } from './emoji-fallback-names.js';

const { dayjs, u } = converse.env;
const { convertASCII2Emoji, getShortnameReferences, getCodePointReferences } = u;

/**
 * Name of the bundled Twemoji image for this emoji (dist/images/emoji/<name>.png),
 * or null if there is none. Twemoji's naming: code points joined by '-', with
 * FE0F removed unless the sequence contains a ZWJ (U+200D).
 * @param {string} emoji
 * @returns {string|null}
 */
function bundledEmojiName(emoji) {
    const cps = Array.from(emoji).map((c) => c.codePointAt(0).toString(16));
    const name = (cps.includes('200d') ? cps : cps.filter((c) => c !== 'fe0f')).join('-');
    return BUNDLED_EMOJI.has(name) ? name : null;
}

/** @type {Map<string, boolean>} */
const _systemDrawsCache = new Map();
/** @type {CanvasRenderingContext2D|null|undefined} */
let _probeCtx;

/**
 * Can the system fonts really draw this emoji? An emoji newer than the OS font
 * (e.g. Emoji 13+ on Windows 10) shows as an empty box -- grey, monochrome --
 * or, for sequences, as several separate glyphs. Both are detected on a small
 * canvas: a properly drawn emoji is one glyph with colourful pixels.
 * Fails open (true) if canvas isn't usable, so we never replace a working glyph
 * because of a probe failure.
 * @param {string} emoji
 * @returns {boolean}
 */
function systemDrawsEmoji(emoji) {
    const cached = _systemDrawsCache.get(emoji);
    if (cached !== undefined) return cached;
    let result = true;
    try {
        if (_probeCtx === undefined) {
            const canvas = document.createElement('canvas');
            canvas.width = 96;
            canvas.height = 48;
            _probeCtx = canvas.getContext('2d', { willReadFrequently: true });
        }
        const ctx = _probeCtx;
        if (ctx) {
            const SIZE = 32;
            ctx.clearRect(0, 0, 96, 48);
            ctx.font = `${SIZE}px "Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji","Twemoji Mozilla",sans-serif`;
            ctx.textBaseline = 'top';
            const too_wide = ctx.measureText(emoji).width > SIZE * 1.5; // sequence split into parts
            ctx.fillText(emoji, 2, 2);
            const { data } = ctx.getImageData(0, 0, 96, 48);
            let coloured = 0;
            for (let i = 0; i < data.length; i += 4) {
                if (data[i + 3] < 80) continue;
                const r = data[i], g = data[i + 1], b = data[i + 2];
                if (Math.max(r, g, b) - Math.min(r, g, b) > 24) coloured++;
            }
            result = !too_wide && coloured >= 12;
        }
    } catch (e) {
        result = true;
    }
    _systemDrawsCache.set(emoji, result);
    return result;
}

export function isMobileViewport() {
    return window.innerWidth <= MOBILE_CUTOFF;
}

/**
 * @param {import('@converse/headless/types/shared/chatbox').default} model
 */
export function getChatStyle(model) {
    if (isMobileViewport()) return '';
    const { height, width } = model.toJSON();
    const is_overlayed = api.settings.get('view_mode') === 'overlayed';
    return is_overlayed ? `${width ? `width: ${width}px;` : ''}${height ? `height: ${height}px;` : ''}` : '';
}

/**
 * @param {import('@converse/headless').Model} model
 */
export function getUnreadMsgsDisplay(model) {
    const num_unread = model.get('num_unread') || 0;
    return num_unread < 100 ? num_unread : '99+';
}

/**
 * @param {Promise<HeadingButtonAttributes>|HeadingButtonAttributes} promise_or_data
 * @returns {Promise<TemplateResult|''>}
 */
export async function getHeadingDropdownItem(promise_or_data) {
    const data = await promise_or_data;
    return data
        ? html`<a href="#" role="button" class="dropdown-item ${data.a_class}" @click="${data.handler}" title="${data.i18n_title}"><converse-icon size="1em" class="fa ${data.icon_class}"></converse-icon>${data.i18n_text}</a>`
        : '';
}

/**
 * @param {Promise<HeadingButtonAttributes>|HeadingButtonAttributes} promise_or_data
 * @returns {Promise<TemplateResult>}
 */
export async function getHeadingStandaloneButton(promise_or_data) {
    const data = await promise_or_data;
    return html`<button type="button" class="btn chatbox-btn ${data.a_class}" @click="${data.handler}" title="${data.i18n_title}"><converse-icon size="1em" class="fa ${data.icon_class}"></converse-icon></button>`;
}

/**
 * @param {Promise<Array<HeadingButtonAttributes>>} promise
 */
export async function getStandaloneButtons(promise) {
    const btns = await promise;
    return btns
        .filter((b) => b.standalone)
        .map((b) => getHeadingStandaloneButton(b))
        .reverse()
        .map((b) => until(b, ''));
}

/**
 * @param {Promise<Array<object>>} promise
 */
export async function getDropdownButtons(promise) {
    const btns = await promise;
    const dropdown_btns = btns.filter((b) => !b.standalone).map((b) => getHeadingDropdownItem(b));
    return dropdown_btns.length
        ? html`<converse-dropdown class="chatbox-btn btn-group dropstart" .items="${dropdown_btns}"></converse-dropdown>`
        : '';
}

export function onScrolledDown(model) {
    if (!model.isHidden()) {
        if (api.settings.get('allow_url_history_change')) {
            // Clear location hash if set to one of the messages in our history
            const hash = window.location.hash;
            if (hash && model.messages.get(hash.slice(1))) {
                history.pushState(null, '', window.location.pathname);
            }
        }
    }
}

/**
 * Given a message object, returns a TemplateResult indicating a new day if
 * the passed in message is more than a day later than its predecessor.
 * @param {Message} message
 * @returns {TemplateResult|undefined}
 */
export function getDayIndicator(message) {
    const messages = message.collection?.models;
    if (!messages) {
        return;
    }
    const idx = messages.indexOf(message);
    const prev_message = messages[idx - 1];
    if (!prev_message || dayjs(message.get('time')).isAfter(dayjs(prev_message.get('time')), 'day')) {
        const day_date = dayjs(message.get('time')).startOf('day');
        return tplNewDay({
            'type': 'date',
            'time': day_date.toISOString(),
            'datestring': day_date.format('dddd MMM Do YYYY'),
        });
    }
}

/**
 * @param {MUCMessage} message
 */
export function getHats(message) {
    if (message.get('type') === 'groupchat') {
        const allowed_hats = api.settings
            .get('muc_hats')
            .filter((hat) => hat)
            .map((hat) => hat.toLowerCase());
        let vcard_roles = [];
        if (allowed_hats.includes('vcard_roles')) {
            vcard_roles = message.vcard ? message.vcard.get('role') : null;
            vcard_roles = vcard_roles
                ? vcard_roles
                      .split(',')
                      .filter((hat) => hat)
                      .map((hat) => ({ title: hat }))
                : [];
        }
        const muc_role = message.occupant ? [message.occupant.get('role')] : [];
        const muc_affiliation = message.occupant ? [message.occupant.get('affiliation')] : [];

        const affiliation_role_hats = [...muc_role, ...muc_affiliation]
            .filter((hat) => hat)
            .filter((hat) => allowed_hats.includes(hat.toLowerCase()))
            .map((hat) => ({ title: hat }));
        const hats = allowed_hats.includes('xep317') ? message.occupant?.get('hats') || [] : [];
        return [...hats, ...vcard_roles, ...affiliation_role_hats];
    }
    return [];
}

export function getTonedEmojis() {
    if (!converse.emojis.toned) {
        const people = converse.emojis.json?.people;
        if (!people) {
            // emoji.json failed to load (e.g. missing from an app package) —
            // don't crash every message/picker render over it, just skip
            // skin-tone support until it's available.
            console.warn('getTonedEmojis: emoji data not loaded, skipping tone support');
            return [];
        }
        converse.emojis.toned = u.unique(
            Object.values(people)
                .filter((person) => person.sn.includes('_tone'))
                .map((person) => person.sn.replace(/_tone[1-5]/, '')),
        );
    }
    return converse.emojis.toned;
}

/**
 * @typedef {object} EmojiMarkupOptions
 * @property {boolean} [unicode_only=false]
 * @property {boolean} [add_title_wrapper=false]
 *
 * @param {object} data
 * @param {EmojiMarkupOptions} options
 */
export function getEmojiMarkup(data, options = { unicode_only: false }) {
    const emoji = data.emoji;
    const cp = data.cp; // '1fae1' или '1fa77'
    const shortname = data.shortname || '';
    const assets_path = api.settings.get('assets_path') || '/dist';

    if (cp === '1fae1' || cp === '1fa77') {
        const title = shortname || (cp === '1fae1' ? ':saluting_face:' : ':pink_heart:');
        const finalPath = `${assets_path}/./images/custom_emojis/${cp}.png`;

        console.log('Detected new emoji! Force path:', finalPath);

        return html`<img class="emoji" loading="lazy" draggable="false" title="${title}" alt="${title}" src="${finalPath}">`;
    }

    const emojiData = api.settings.get('emoji_data') || { custom: [] };
    let customEmoji =
        converse.emojis.by_sn[shortname] || emojiData.custom.find((e) => e.sn === shortname || e.sn === emoji);

    if (customEmoji && customEmoji.url) {
        const url = customEmoji.url;
        const finalSrc = url.startsWith('.') ? `${assets_path}/${url}` : url;
        console.log('Found in Custom:', customEmoji.sn, 'Path:', finalSrc);

        return html`<img class="emoji" loading="lazy" draggable="false" title="${customEmoji.sn || shortname}" alt="${customEmoji.sn || shortname}" src="${finalSrc}">`;
    }

    // 2b. Emoji newer than the user's OS font (Emoji 13+ on Windows 10 draws an
    // empty box): use the bundled Twemoji image, but only when the system
    // really can't draw it. If the image fails to load, fall back to the text.
    if (emoji && !options.unicode_only) {
        const bundled = bundledEmojiName(emoji);
        if (bundled && !systemDrawsEmoji(emoji)) {
            const base = String(assets_path).replace(/\/+$/, '');
            return html`<img class="emoji" loading="lazy" draggable="false" title="${shortname}" alt="${emoji}" src="${base}/images/emoji/${bundled}.png" @error="${(/** @type {Event} */ ev) =>
                    /** @type {HTMLElement} */ (ev.target).replaceWith(document.createTextNode(emoji))}">`;
        }
    }

    // 3. ФОЛБЕК ДЛЯ СТАНДАРТНЫХ
    if (emoji && cp) {
        if (options.unicode_only) return emoji;
        if (api.settings.get('use_system_emojis')) {
            return html`<span class="emoji-system">${emoji}</span>`;
        }

        const path = api.settings.get('emoji_image_path');
        return html`<img class="emoji" loading="lazy" src="${path}/72x72/${cp}.png" alt="${emoji}">`;
    }

    return emoji || shortname;
}
// export function getEmojiMarkup(data, options = { unicode_only: false, add_title_wrapper: false }) {
//     const emoji = data.emoji;
//     const shortname = data.shortname;
//     const cp = data.cp;
//     const isSystem = api.settings.get('use_system_emojis');

//     // 1. ПРИОРИТЕТ CUSTOM: Сначала ищем в JSON по shortname
//     const customEmoji = converse.emojis.by_sn[shortname];
//     console.log(data, customEmoji, isSystem);
//     // Если в JSON найден URL для этого shortname — это наш кастомный PNG
//     if (customEmoji && customEmoji.url) {
//         const url = customEmoji.url;
//         return html`<img
//             class="emoji"
//             loading="lazy"
//             draggable="false"
//             title="${shortname}"
//             alt="${shortname}"
//             src="${url.startsWith('.') ? `${api.settings.get('assets_path')}/${url}` : url}"
//         />`;
//     }

//     // 2. СИСТЕМНЫЕ / СТАНДАРТНЫЕ: Если не нашли в custom, но есть символ/код
//     if (emoji && cp) {
//         if (options.unicode_only) return emoji;

//         // Если включены системные эмодзи — рендерим текстом в span
//         if (isSystem) {
//             return html`<span class="emoji-system" title="${shortname || ''}">${emoji}</span>`;
//         }

//         // Если системные выключены — рендерим стандартный PNG из папки 72x72
//         const path = api.settings.get('emoji_image_path');
//         return html`<img
//             class="emoji"
//             loading="lazy"
//             src="${path}/72x72/${cp}.png"
//             alt="${emoji}"
//             title="${shortname}"
//         />`;
//     }

//     // 3. ЗАПАСНОЙ ВАРИАНТ
//     return options.unicode_only ? shortname : emoji || shortname;
// }

/**
 * Обрабатывает текст и заменяет короткие имена/коды на эмодзи
 */

// export function getEmojiMarkup(data, options = { unicode_only: false, add_title_wrapper: false }) {
//     const emoji = data.emoji;
//     const shortname = data.shortname;
//     console.log(data, (options = { unicode_only: false, add_title_wrapper: false }));
//     if (emoji) {
//         if (options.unicode_only) {
//             return emoji;
//         } else if (api.settings.get('use_system_emojis')) {
//             if (options.add_title_wrapper) {
//                 return shortname ? html`<span title="${shortname}">${emoji}</span>` : emoji;
//             } else {
//                 return emoji;
//             }
//         } else {
//             const path = api.settings.get('emoji_image_path');
//             return html`<img
//                 class="emoji"
//                 loading="lazy"
//                 draggable="false"
//                 title="${shortname}"
//                 alt="${emoji}"
//                 src="${path}/72x72/${data.cp}.png"
//             />`;
//         }
//     } else if (options.unicode_only) {
//         return shortname;
//     } else {
//         const { url } = converse.emojis.by_sn[shortname];
//         return html`<img
//             class="emoji"
//             loading="lazy"
//             draggable="false"
//             title="${shortname}"
//             alt="${shortname}"
//             src="${url.startsWith('.') ? `${api.settings.get('assets_path')}/${url}` : url}"
//         />`;
//     }
// }

// /**
//  * @param {string} text
//  * @param {object} options
//  */
// export function addEmojisMarkup(text, options) {
//     let list = [text];
//     [...getShortnameReferences(text), ...getCodePointReferences(text)]
//         .sort((a, b) => b.begin - a.begin)
//         .forEach((ref) => {
//             const text = list.shift();
//             const emoji = getEmojiMarkup(ref, options);
//             if (typeof emoji === 'string') {
//                 list = [text.slice(0, ref.begin) + emoji + text.slice(ref.end), ...list];
//             } else {
//                 list = [text.slice(0, ref.begin), emoji, text.slice(ref.end), ...list];
//             }
//         });
//     return list;
// }

export function addEmojisMarkup(text, options) {
    let list = [text];
    let refs = [...getShortnameReferences(text), ...getCodePointReferences(text)];

    // ХАК ДЛЯ НОВЫХ ЭМОДЗИ (iOS)
    const newEmojis = [
        { char: '🫡', cp: '1FAE1', sn: ':saluting_face:' },
        { char: '🩷', cp: '1FA77', sn: ':pink_heart:' },
    ];

    newEmojis.forEach((e) => {
        if (text.includes(e.char)) {
            if (!refs.some((r) => r.emoji === e.char)) {
                let pos = text.indexOf(e.char);
                while (pos !== -1) {
                    refs.push({
                        begin: pos,
                        end: pos + e.char.length,
                        emoji: e.char,
                        cp: e.cp,
                        shortname: e.sn,
                        is_manual: true, // флаг для лога
                    });
                    pos = text.indexOf(e.char, pos + 1);
                }
            } else {
            }
        }
    });

    refs.sort((a, b) => b.begin - a.begin).forEach((ref) => {
        const textContent = list.shift();
        const emojiMarkup = getEmojiMarkup(ref, options);

        if (typeof emojiMarkup === 'string') {
            list = [textContent.slice(0, ref.begin) + emojiMarkup + textContent.slice(ref.end), ...list];
        } else {
            list = [textContent.slice(0, ref.begin), emojiMarkup, textContent.slice(ref.end), ...list];
        }
    });

    return list;
}

/**
 * Returns an emoji represented by the passed in shortname.
 * Scans the passed in text for shortnames and replaces them with
 * emoji unicode glyphs or alternatively if it's a custom emoji
 * without unicode representation then a lit TemplateResult
 * which represents image tag markup is returned.
 *
 * The shortname needs to be defined in `emojis.json`
 * and needs to have either a `cp` attribute for the codepoint, or
 * an `url` attribute which points to the source for the image.
 *
 * @namespace u
 * @method u.shortnamesToEmojis
 * @param {String} str - String containing the shortname(s)
 * @param {Object} options
 * @param {Boolean} options.unicode_only - Whether emojis are rendered as
 *  unicode codepoints. If so, the returned result will be an array
 *  with containing one string, because the emojis themselves will
 *  also be strings. If set to false, emojis will be represented by
 *  lit TemplateResult objects.
 * @param {Boolean} options.add_title_wrapper - Whether unicode
 *  codepoints should be wrapped with a `<span>` element with a
 *  title, so that the shortname is shown upon hovering with the
 *  mouse.
 * @returns {Array} An array of at least one string, or otherwise
 * strings and lit TemplateResult objects.
 */
export function shortnamesToEmojis(str, options = { unicode_only: false, add_title_wrapper: false }) {
    str = convertASCII2Emoji(str);
    return addEmojisMarkup(str, options);
}

Object.assign(u, { shortnamesToEmojis });
